import type { EraDef } from '../types';

/**
 * ERA 9 — NEAR FUTURE
 * A neon megacity block under martial-law lockdown, fought against the city's own
 * security net. Nothing here moves diagonally: recon and interceptor drones drive
 * the street grid in right-angle dog-legs, converging on your row and column.
 * Sentry turrets and enforcer mechs root, paint a designator on you, then FREEZE
 * their bearing and fire a laser lance down the line you were standing on half a
 * second ago. Denial pylons energize the pavement in square city blocks that are
 * aligned to the world axes, not to you. Black-violet streets under magenta
 * signage. Boss: War Mech Zero, whose guns are bolted-on weapon pods you can shoot
 * off its hull — every heavy volley comes from a pod, and while any pod lives the
 * mech shrugs off part of your damage. Palette: near-black violet with magenta
 * signage; enemy bullets hot magenta (0xff2ad4) against arc-cyan player fire;
 * hazards ion-green (0x9dff3a) — delivered as WORLD-AXIS LATTICES of stalled tiles
 * with walkable lanes, not painted player-facing lines (era 7) or fused
 * detonations and vent rings (era 8).
 * Signature behaviors owned here: `lockon`, `gridstep`. Boss special: `subsystems`.
 */
const ENEMY_BULLET = 0xff2ad4;
const HAZARD = 0x9dff3a;

export const ERA_CYBERPUNK: EraDef = {
  index: 8,
  id: 'cyberpunk',
  name: 'Near Future',
  subtitle: '2077 AD',
  implemented: true,
  palette: {
    sky: 0x08050f,
    fog: 0x120a1f,
    fogDensity: 0.024,
    ground: 0x0a0812,
    groundAlt: 0x140f22,
    ambient: 0x33205a,
    sun: 0xff5cf5,
    sunIntensity: 1.3,
    accent: 0x5cf0ff,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'megacity',
    arenaRadius: 72,
    groundStyle: 'grid',
    props: [
      { visual: { geo: 'box', color: 0x140f22, emissive: 0xff2ad4, emissiveIntensity: 0.45, scaleY: 6.0, anim: 'none' }, count: 24, scale: [0.5, 2.6], y: 0 },
      { visual: { geo: 'disc', color: 0x5cf0ff, emissive: 0x5cf0ff, emissiveIntensity: 1.1, scaleY: 0.2, anim: 'spin' }, count: 14, scale: [0.35, 0.8], y: 1.6 },
      { visual: { geo: 'capsule', color: 0x1e1630, emissive: HAZARD, emissiveIntensity: 0.8, scaleY: 2.4, anim: 'pulse' }, count: 12, scale: [0.2, 0.45], y: 0.4 },
    ],
  },
  enemies: [
    {
      id: 'c9_recon',
      name: 'Recon Drone',
      archetype: 'swarmer',
      hp: 36,
      speed: 5.6,
      damage: 11,
      radius: 0.38,
      xp: 10,
      visual: { geo: 'octa', color: 0xff5cf5, emissive: ENEMY_BULLET, emissiveIntensity: 0.8, scale: 0.72, anim: 'spin' },
      behavior: { type: 'gridstep', switchTime: 1.1, laneWidth: 1.2, speedMult: 1.0 },
      // Signature swarmer: no patterns — pure bodies that drive the streets in right-angle dog-legs,
      // 5.6 u/s raw but only ~0.7x that as closing speed because it walks the long L path.
      // Counterplay: never back straight down a lane; a diagonal walk splits the whole flock.
    },
    {
      id: 'c9_interceptor',
      name: 'Interceptor Drone',
      archetype: 'shooter',
      hp: 58,
      speed: 4.2,
      damage: 12,
      radius: 0.46,
      xp: 15,
      visual: { geo: 'spike', color: 0xbf5cff, emissive: ENEMY_BULLET, emissiveIntensity: 0.6, scale: 0.95, anim: 'spin' },
      behavior: { type: 'gridstep', switchTime: 1.6, laneWidth: 1.6, speedMult: 0.85 },
      patterns: ['c9_micro_missiles'],
      patternCooldown: 2.4,
      fireRange: 17,
      // Signature: the armed version of the swarm — fires two pairs of slow-then-accelerating micro-missiles
      // with light homing while it dog-legs. Counterplay: the missiles only turn at 1.2 rad/s, so cut across
      // their nose; break its lane and the volley leads a position you already left.
    },
    {
      id: 'c9_sentry',
      name: 'Sentry Turret',
      archetype: 'shooter',
      hp: 70,
      speed: 2.4,
      damage: 12,
      radius: 0.52,
      xp: 17,
      shardChance: 0.1,
      visual: { geo: 'box', color: 0x3a2050, emissive: ENEMY_BULLET, emissiveIntensity: 0.6, scale: 1.0, scaleY: 1.15, anim: 'none' },
      behavior: { type: 'lockon', range: 15, trackTime: 1.0, lockTime: 0.55, lockPattern: 'c9_lance', cooldown: 1.8, driftSpeedMult: 0.55 },
      // Signature: no patterns at all — it crawls to 15 u, roots, tracks you for 1 s, then the lock SNAPS
      // (facing freezes, body pulses red) and 0.55 s later a 22 u/s needle bar rips down that exact line.
      // Counterplay: the track is a free burst window; the red pulse is your cue to take one step sideways.
    },
    {
      id: 'c9_pylon',
      name: 'Denial Pylon',
      archetype: 'shooter',
      hp: 78,
      speed: 0,
      damage: 12,
      radius: 0.62,
      xp: 17,
      shardChance: 0.12,
      visual: { geo: 'capsule', color: 0x241a38, emissive: HAZARD, emissiveIntensity: 0.85, scale: 1.0, scaleY: 1.4, anim: 'pulse' },
      behavior: { type: 'stationary' },
      patterns: ['c9_grid_lattice'],
      patternCooldown: 2.2,
      fireRange: 14,
      // The era's hazard author: energizes the pavement in two crossing rows of square ion tiles that are
      // locked to the world axes (never to you), leaving ~1.3 u lanes plus one dead slot per row.
      // Counterplay: it can never chase — learn the two compass rows, walk the lanes, and kill it from
      // outside 14 u, or give the block up entirely before the cross-hatch closes.
    },
    {
      id: 'c9_suppressor',
      name: 'Riot Suppressor',
      archetype: 'tank',
      hp: 155,
      speed: 2.6,
      damage: 22,
      radius: 0.95,
      xp: 28,
      shardChance: 0.15,
      visual: { geo: 'box', color: 0x1e1630, emissive: HAZARD, emissiveIntensity: 0.4, scale: 1.5, scaleY: 0.9, anim: 'none' },
      behavior: { type: 'charge', windup: 0.75, speedMult: 2.5, duration: 1.0, cooldown: 2.6 },
      trail: { radius: 1.0, duration: 3.2, dps: 8, interval: 0.28, color: HAZARD },
      onDeath: { pattern: 'c9_coolant_burst' },
      // Filler charger with teeth (the era's one trailing enemy): a cracked reactor sled that leaks ionized
      // coolant everywhere it drives and vents it when it dies. Counterplay: read the 0.75 s windup like any
      // charger, fight it in open street because its old lanes stay lethal, and never finish it point-blank.
    },
    {
      id: 'c9_enforcer',
      name: 'Enforcer Mech',
      archetype: 'tank',
      hp: 145,
      speed: 2.2,
      damage: 20,
      radius: 0.90,
      xp: 30,
      shardChance: 0.16,
      visual: { geo: 'blob', color: 0x2a1c40, emissive: ENEMY_BULLET, emissiveIntensity: 0.5, scale: 1.45, scaleY: 1.1, anim: 'bob' },
      behavior: { type: 'lockon', range: 20, trackTime: 1.4, lockTime: 0.7, lockPattern: 'c9_heavy_lance', cooldown: 2.6, driftSpeedMult: 0.4 },
      // Signature heavy, and the boss's rehearsal: it locks from 20 u — outside your 16 u weapon range — so
      // you must walk INTO it, and its lance is a 5-needle bar plus a trailing 7-shard cone down the same
      // frozen bearing. Counterplay: close during the 1.4 s track (it only creeps at 0.88 u/s), then step
      // fully off the line on the red pulse — a small sidestep still eats the cone.
    },
    {
      id: 'c9_warden',
      name: 'ICE Warden',
      archetype: 'elite',
      elite: true,
      hp: 100,
      speed: 4.0,
      damage: 17,
      radius: 0.72,
      xp: 36,
      shardChance: 0.4,
      visual: { geo: 'torus', color: 0x5cf0ff, emissive: ENEMY_BULLET, emissiveIntensity: 0.9, scale: 1.3, anim: 'spin' },
      behavior: { type: 'gridstep', switchTime: 0.8, laneWidth: 1.4, speedMult: 1.15 },
      patterns: ['c9_data_spiral', 'c9_micro_missiles'],
      patternCooldown: 2.0,
      fireRange: 18,
      // Elite: the swarm's network node — the fastest grid-runner in the era (0.8 s turns make its dog-legs
      // read like strafing) alternating a counter-rotating ring spiral with missile pairs.
      // Counterplay: it always turns at right angles, so its next leg is predictable; hold a diagonal and
      // punish the spiral's rotation gap instead of chasing it around a corner.
    },
    // Boss weapon pod (War Mech Zero, all three phases): stationary autocannon rack bolted to the hull ring.
    // It has no patterns of its own — the boss's `subsystems` special fires from each live pod, and while any
    // pod lives the mech ignores part of your damage. Counterplay: pods are the softest thing in the fight;
    // clear the rack to switch that volley off, or ignore them and race the HP bar instead.
    {
      id: 'c9_pod',
      name: 'Autocannon Pod',
      archetype: 'shooter',
      hp: 90,
      speed: 0,
      damage: 10,
      radius: 0.50,
      xp: 10,
      shardChance: 0.05,
      visual: { geo: 'torus', color: 0x5cf0ff, emissive: ENEMY_BULLET, emissiveIntensity: 1.0, scale: 0.9, scaleY: 0.7, anim: 'pulse' },
      behavior: { type: 'stationary' },
    },
  ],
  patterns: [
    {
      id: 'c9_lance',
      emitters: [
        { shape: 'wall', interval: 1, bursts: 1, count: 3, spread: 0.8,
          bullet: { speed: 22, lifetime: 1.6, radius: 0.22, damage: 16, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_heavy_lance',
      emitters: [
        { shape: 'wall', start: 0, interval: 1, bursts: 1, count: 5, spread: 1.6,
          bullet: { speed: 24, lifetime: 1.8, radius: 0.26, damage: 19, visual: 'needle', color: ENEMY_BULLET } },
        { shape: 'aimed', start: 0.22, interval: 1, bursts: 1, count: 7, spread: 0.55,
          bullet: { speed: 14, lifetime: 2.0, radius: 0.24, damage: 15, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_micro_missiles',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.35, bursts: 2, count: 2, spread: 0.5,
          bullet: { speed: 7.5, accel: 4.0, maxSpeed: 13, homing: 1.2, homingDelay: 0.35, lifetime: 2.6, radius: 0.26, damage: 14, visual: 'star', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_grid_lattice',
      emitters: [
        { shape: 'wall', aim: false, angle: 0, start: 0, interval: 1.3, bursts: 2, count: 4, spread: 12, gaps: 1,
          bullet: { speed: 7, accel: -9, minSpeed: 0.1, lifetime: 1.2, radius: 0.30, damage: 12, visual: 'ring', color: HAZARD,
                    linger: { radius: 1.35, duration: 3.8, dps: 12, color: HAZARD } } },
        { shape: 'wall', aim: false, angle: 1.5708, start: 0.65, interval: 1.3, bursts: 2, count: 4, spread: 12, gaps: 1,
          bullet: { speed: 7, accel: -9, minSpeed: 0.1, lifetime: 1.4, radius: 0.30, damage: 12, visual: 'ring', color: HAZARD,
                    linger: { radius: 1.35, duration: 3.8, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 'c9_data_spiral',
      emitters: [
        { shape: 'spiral', start: 0, interval: 0.24, bursts: 7, count: 6, rotatePerBurst: 0.40,
          bullet: { speed: 6.6, curve: -0.35, lifetime: 2.2, radius: 0.26, damage: 14, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_coolant_burst',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 8,
          bullet: { speed: 3.6, accel: -2.2, minSpeed: 0.3, lifetime: 1.3, radius: 0.34, damage: 15, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.3, duration: 3.0, dps: 11, color: HAZARD } } },
      ],
    },
    {
      id: 'c9_pod_volley',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.22, bursts: 2, count: 2, spread: 0.3,
          bullet: { speed: 11, lifetime: 1.9, radius: 0.22, damage: 13, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_pod_barrage',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.3, bursts: 2, count: 4, spread: 0.7,
          bullet: { speed: 12.5, lifetime: 1.8, radius: 0.24, damage: 14, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_pod_missiles',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.3, bursts: 2, count: 2, spread: 0.45,
          bullet: { speed: 7.0, accel: 4.5, maxSpeed: 14, homing: 1.2, homingDelay: 0.4, lifetime: 2.8, radius: 0.28, damage: 15, visual: 'star', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_systems_fail',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 12,
          bullet: { speed: 7.0, lifetime: 1.7, radius: 0.28, damage: 14, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_boss_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 16,
          bullet: { speed: 6.5, lifetime: 1.9, radius: 0.34, damage: 18, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_boss_lance_sweep',
      emitters: [
        { shape: 'spiral', start: 0, interval: 0.30, bursts: 5, count: 3, rotatePerBurst: 0.60,
          bullet: { speed: 18, lifetime: 1.7, radius: 0.24, damage: 16, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_boss_grid_cage',
      emitters: [
        { shape: 'wall', aim: false, angle: 0, start: 0, interval: 1.5, bursts: 2, count: 6, spread: 20, gaps: 2,
          bullet: { speed: 7.5, accel: -9, minSpeed: 0.1, lifetime: 1.3, radius: 0.32, damage: 15, visual: 'ring', color: HAZARD,
                    linger: { radius: 1.4, duration: 4.0, dps: 13, color: HAZARD } } },
        { shape: 'wall', aim: false, angle: 1.5708, start: 0.7, interval: 1.5, bursts: 2, count: 6, spread: 20, gaps: 2,
          bullet: { speed: 7.5, accel: -9, minSpeed: 0.1, lifetime: 1.5, radius: 0.32, damage: 15, visual: 'ring', color: HAZARD,
                    linger: { radius: 1.4, duration: 4.0, dps: 13, color: HAZARD } } },
      ],
    },
    {
      id: 'c9_boss_missile_salvo',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.40, bursts: 3, count: 3, spread: 0.7,
          bullet: { speed: 8, accel: 4.0, maxSpeed: 14, homing: 1.1, homingDelay: 0.4, lifetime: 2.8, radius: 0.28, damage: 16, visual: 'star', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_boss_overload',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 20,
          bullet: { speed: 7.2, lifetime: 2.0, radius: 0.30, damage: 17, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 12, angle: 0.26,
          bullet: { speed: 4.4, lifetime: 2.6, radius: 0.36, damage: 15, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c9_boss_phase3_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 24,
          bullet: { speed: 7.4, curve: 0.20, lifetime: 2.1, radius: 0.30, damage: 18, visual: 'star', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 14, angle: 0.22,
          bullet: { speed: 4.2, lifetime: 2.8, radius: 0.40, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'wall', aim: false, angle: 0.7854, interval: 1, bursts: 1, count: 6, spread: 22, gaps: 2,
          bullet: { speed: 7.5, accel: -9, minSpeed: 0.1, lifetime: 1.4, radius: 0.32, damage: 16, visual: 'ring', color: HAZARD,
                    linger: { radius: 1.4, duration: 4.5, dps: 13, color: HAZARD } } },
      ],
    },
  ],
  boss: {
    id: 'c9_warmech',
    name: 'War Mech Zero',
    title: 'Autonomous Extinction Unit',
    // Balance: 2900 base HP scales to ~36,250 effective at the era-9 boss stage multiplier (12.50x). This base looks low next to era 8's 3650 on purpose: subsystems removes ~35% of effective damage throughput on average whichever strategy the player picks (clear the pod rack each time, or ignore it and eat the reduction) — both converge to roughly the same ~64-78s fight at the expected level. Tune reduction/redeploy/pod HP before touching this number; raising reduction above ~0.45 makes ignoring the rack strictly wrong and kills the choice.
    hp: 2900,
    radius: 2.7,
    speed: 2.1,
    damage: 40,
    xp: 640,
    visual: { geo: 'box', color: 0x1a1226, emissive: ENEMY_BULLET, emissiveIntensity: 0.9, scale: 1.5, scaleY: 1.25, anim: 'pulse' },
    phases: [
      {
        hpBelow: 1,
        name: 'Diagnostic Sweep',
        behavior: { type: 'keepDistance', distance: 12, strafe: 0.15 },
        patterns: ['c9_boss_lance_sweep', 'c9_boss_grid_cage'],
        patternCooldown: 2.3,
        transition: 1.5,
        onEnter: 'c9_boss_enter',
        special: { type: 'subsystems', enemy: 'c9_pod', count: 2, orbitRadius: 4.0, angular: 0.35,
                   reduction: 0.25, podPattern: 'c9_pod_volley', podInterval: 3.2,
                   redeploy: 10.0, deployTelegraph: 1.0, exposedPattern: 'c9_systems_fail' },
      },
      {
        hpBelow: 0.66,
        name: 'Combat Protocol',
        behavior: { type: 'chase' },
        speedMult: 1.1,
        patterns: ['c9_boss_lance_sweep', 'c9_boss_missile_salvo', 'c9_boss_grid_cage'],
        patternCooldown: 1.9,
        transition: 2,
        onEnter: 'c9_boss_overload',
        summons: { enemy: 'c9_recon', count: 4, interval: 9 },
        special: { type: 'subsystems', enemy: 'c9_pod', count: 3, orbitRadius: 4.2, angular: 0.55,
                   reduction: 0.35, podPattern: 'c9_pod_barrage', podInterval: 2.2,
                   redeploy: 7.0, deployTelegraph: 0.85, exposedPattern: 'c9_systems_fail' },
      },
      {
        hpBelow: 0.32,
        name: 'Extinction Directive',
        behavior: { type: 'orbit', radius: 7, angular: 0.90 },
        speedMult: 1.2,
        patterns: ['c9_boss_lance_sweep', 'c9_boss_missile_salvo', 'c9_boss_grid_cage'],
        patternCooldown: 1.5,
        transition: 2,
        onEnter: 'c9_boss_phase3_enter',
        summons: { enemy: 'c9_recon', count: 5, interval: 7 },
        special: { type: 'subsystems', enemy: 'c9_pod', count: 4, orbitRadius: 4.5, angular: -0.80,
                   reduction: 0.40, podPattern: 'c9_pod_missiles', podInterval: 1.7,
                   redeploy: 5.5, deployTelegraph: 0.70, exposedPattern: 'c9_systems_fail' },
      },
    ],
  },
  stages: [
    {
      name: 'Neon Arterials',
      duration: 225,
      objective: { type: 'collect', count: 90, label: 'Recover stolen data shards' },
      density: { start: 1.3, end: 3.2 },
      pool: [
        { enemy: 'c9_recon', weight: 10 },
        { enemy: 'c9_interceptor', weight: 4, minTime: 20 },
        { enemy: 'c9_sentry', weight: 3, minTime: 40 },
        { enemy: 'c9_pylon', weight: 3, minTime: 55 },
        { enemy: 'c9_suppressor', weight: 2, minTime: 85 },
        { enemy: 'c9_enforcer', weight: 1, minTime: 110 },
      ],
      waves: [
        { t: 5, enemy: 'c9_recon', count: 6, formation: 'ring' },
        { t: 22, enemy: 'c9_interceptor', count: 3, formation: 'line' },
        { t: 45, enemy: 'c9_recon', count: 18, formation: 'cluster' },
        { t: 65, enemy: 'c9_sentry', count: 3, formation: 'random' },
        { t: 88, enemy: 'c9_pylon', count: 3, formation: 'line' },
        { t: 112, enemy: 'c9_enforcer', count: 1, formation: 'edge' },
        { t: 140, enemy: 'c9_interceptor', count: 5, formation: 'random' },
        { t: 168, enemy: 'c9_suppressor', count: 2, formation: 'edge' },
        { t: 200, enemy: 'c9_warden', count: 1, formation: 'edge' },
      ],
    },
    {
      name: 'Corporate Curfew',
      duration: 255,
      objective: { type: 'hold', seconds: 100, radius: 8, label: 'Hold the broadcast node' },
      density: { start: 1.7, end: 3.8 },
      pool: [
        { enemy: 'c9_recon', weight: 8 },
        { enemy: 'c9_interceptor', weight: 5 },
        { enemy: 'c9_sentry', weight: 4 },
        { enemy: 'c9_pylon', weight: 4 },
        { enemy: 'c9_suppressor', weight: 3, minTime: 30 },
        { enemy: 'c9_enforcer', weight: 2, minTime: 50 },
        { enemy: 'c9_warden', weight: 1, minTime: 90 },
      ],
      waves: [
        { t: 5, enemy: 'c9_sentry', count: 4, formation: 'ring' },
        { t: 32, enemy: 'c9_enforcer', count: 2, formation: 'edge' },
        { t: 60, enemy: 'c9_pylon', count: 4, formation: 'ring' },
        { t: 90, enemy: 'c9_interceptor', count: 6, formation: 'cluster' },
        { t: 118, enemy: 'c9_warden', count: 1, formation: 'edge' },
        { t: 148, enemy: 'c9_recon', count: 30, formation: 'ring' },
        { t: 175, enemy: 'c9_suppressor', count: 3, formation: 'line' },
        { t: 208, enemy: 'c9_enforcer', count: 3, formation: 'edge' },
        { t: 236, enemy: 'c9_warden', count: 2, formation: 'line' },
      ],
    },
    {
      name: 'Zero Tower Plaza',
      duration: 600,
      density: { start: 1.0, end: 1.0 },
      pool: [
        { enemy: 'c9_recon', weight: 6 },
        { enemy: 'c9_interceptor', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'c9_recon', count: 10, formation: 'ring' }],
      boss: { id: 'c9_warmech', at: 20 },
    },
  ],
  loot: [
    { itemId: 'a_phaseweave', weight: 2 },
    { itemId: 'r_neon_core', weight: 2 },
    { itemId: 'w_pulse_rifle', weight: 2 },
    { itemId: 'w_flintlock', weight: 1 },
    { itemId: 'a_hide', weight: 2 },
    { itemId: 'r_flagellum', weight: 1 },
    { itemId: 'r_trilobite_eye', weight: 1 },
  ],
  audio: { music: 'music/era9_cyberpunk', ambient: 'amb/megacity', sfxSet: 'synthetic' },
};
